// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { useState } from "react";

const VISITS = [
  { id:1, date:"2026-02-06", client:"Canstorr Commodities", location:"Wroxton, SK", visitors:["Tyson Becker","Lucas Mason"], purpose:"Quarterly Review", duration:"3h 30m", status:"completed", expenses:850, notes:"Reviewed storage expansion plans. Facility operating at 85% capacity." },
  { id:2, date:"2026-02-04", client:"Western Mining Corp", location:"Saskatoon, SK", visitors:["Lee Mehling"], purpose:"Demo", duration:"2h 15m", status:"completed", expenses:420, notes:"Demonstrated OreSync dashboard and safety tracking features. Strong interest in real-time monitoring." },
  { id:3, date:"2026-02-03", client:"Prairie Health Services", location:"Regina, SK", visitors:["Des Bachynski","Ryan Miskulin"], purpose:"Training", duration:"4h 00m", status:"completed", expenses:1200, notes:"Trained staff on VitaSync platform. 12 users onboarded successfully." },
  { id:4, date:"2026-02-12", client:"Northern Fleet Co", location:"Edmonton, AB", visitors:["Lucas Mason"], purpose:"Initial Assessment", duration:"2h 00m", status:"scheduled", expenses:650, notes:"First meeting to assess fleet management needs." },
  { id:5, date:"2026-02-14", client:"BC Insurance Group", location:"Vancouver, BC", visitors:["Lucas Mason","Ryan Miskulin"], purpose:"Demo", duration:"1h 30m", status:"scheduled", expenses:1800, notes:"PolicyForge product demo for potential enterprise client." },
  { id:6, date:"2026-02-01", client:"AgriTech Farms", location:"Moose Jaw, SK", visitors:["Tyson Becker"], purpose:"Installation Support", duration:"5h 00m", status:"completed", expenses:380, notes:"On-site CropPatrol sensor installation and calibration for grain storage bins." },
  { id:7, date:"2026-01-30", client:"Metro Beauty Collective", location:"Calgary, AB", visitors:["Lucas Mason"], purpose:"Demo", duration:"1h 45m", status:"completed", expenses:550, notes:"Presented GlowDesk to chain of 8 salons. Interested in booking and inventory features." },
  { id:8, date:"2026-02-10", client:"Island Overhead Doors", location:"Victoria, BC", visitors:["Jordan Lutz"], purpose:"Quarterly Review", duration:"2h 00m", status:"cancelled", expenses:0, notes:"Cancelled due to scheduling conflict. Rescheduled to Feb 20." }
];

const PURPOSES = ["All","Demo","Installation Support","Training","Quarterly Review","Initial Assessment"];
const STATUSES = ["All","completed","scheduled","cancelled"];

export default function VisitLogPage() {
  const [visits] = useState(VISITS);
  const [purposeFilter, setPurposeFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [selectedVisit, setSelectedVisit] = useState(null);

  const filtered = visits.filter(v => {
    if (purposeFilter !== "All" && v.purpose !== purposeFilter) return false;
    if (statusFilter !== "All" && v.status !== statusFilter) return false;
    return true;
  });

  const completedVisits = visits.filter(v => v.status === "completed");
  const upcomingVisits = visits.filter(v => v.status === "scheduled");
  const totalExpenses = visits.reduce((sum, v) => sum + v.expenses, 0);
  const avgDuration = completedVisits.length > 0 ? (completedVisits.reduce((sum, v) => {
    const parts = v.duration.match(/(\d+)h\s*(\d+)m/);
    return sum + (parts ? parseInt(parts[1]) * 60 + parseInt(parts[2]) : 0);
  }, 0) / completedVisits.length / 60).toFixed(1) : "0";

  const statusBadge = (status) => {
    const map = { completed:"badge badge-green", scheduled:"badge badge-blue", cancelled:"badge badge-red" };
    return map[status] || "badge";
  };

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Visit Log</h1><p>Client and prospect site visit tracking</p></div>
        <button className="btn btn-primary">+ Log Visit</button>
      </div>

      <div className="stats-grid">
        <div className="stat-card"><div className="stat-label">Visits This Month</div><div className="stat-value">{visits.filter(v => v.date >= "2026-02-01").length}</div></div>
        <div className="stat-card"><div className="stat-label">Upcoming</div><div className="stat-value" style={{color:"#3b82f6"}}>{upcomingVisits.length}</div></div>
        <div className="stat-card"><div className="stat-label">Avg Duration</div><div className="stat-value">{avgDuration}h</div></div>
        <div className="stat-card"><div className="stat-label">Total Travel Cost</div><div className="stat-value" style={{color:"#f97316"}}>${totalExpenses.toLocaleString()}</div></div>
      </div>

      <div style={{display:"flex",gap:12,marginBottom:16}}>
        <select className="input" value={purposeFilter} onChange={e => setPurposeFilter(e.target.value)} style={{width:200}}>
          {PURPOSES.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select className="input" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{width:160}}>
          {STATUSES.map(s => <option key={s} value={s}>{s === "All" ? "All Statuses" : s}</option>)}
        </select>
      </div>

      <div className="card">
        <div className="card-header"><h3>Visit Log</h3><span style={{fontSize:12,color:"#64748b"}}>{filtered.length} visits</span></div>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead>
              <tr style={{borderBottom:"2px solid #334155"}}>
                {["Date","Client","Location","Visitor(s)","Purpose","Duration","Status","Expenses"].map(h => (
                  <th key={h} style={{textAlign:"left",padding:"10px 12px",fontSize:12,fontWeight:700,color:"#94a3b8",whiteSpace:"nowrap"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(v => (
                <tr key={v.id} style={{borderBottom:"1px solid #334155",cursor:"pointer"}} onClick={() => setSelectedVisit(v)}>
                  <td style={{padding:"10px 12px",fontSize:13,color:"#e2e8f0"}}>{v.date}</td>
                  <td style={{padding:"10px 12px",fontSize:13,fontWeight:600,color:"#e2e8f0"}}>{v.client}</td>
                  <td style={{padding:"10px 12px",fontSize:12,color:"#94a3b8"}}>{v.location}</td>
                  <td style={{padding:"10px 12px",fontSize:12,color:"#94a3b8"}}>{v.visitors.join(", ")}</td>
                  <td style={{padding:"10px 12px"}}><span className="badge badge-purple">{v.purpose}</span></td>
                  <td style={{padding:"10px 12px",fontSize:13,color:"#e2e8f0"}}>{v.duration}</td>
                  <td style={{padding:"10px 12px"}}><span className={statusBadge(v.status)}>{v.status}</span></td>
                  <td style={{padding:"10px 12px",fontSize:13,color:v.expenses > 0 ? "#f97316" : "#64748b"}}>{v.expenses > 0 ? "$" + v.expenses : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedVisit && (
        <div className="modal-overlay" onClick={() => setSelectedVisit(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedVisit.client}</h2>
              <button className="modal-close" onClick={() => setSelectedVisit(null)}>X</button>
            </div>
            <div className="modal-body">
              <div style={{display:"flex",gap:16,marginBottom:16,flexWrap:"wrap"}}>
                <span className={statusBadge(selectedVisit.status)}>{selectedVisit.status}</span>
                <span className="badge badge-purple">{selectedVisit.purpose}</span>
                <span style={{fontSize:13,color:"#94a3b8"}}>{selectedVisit.date}</span>
                <span style={{fontSize:13,color:"#94a3b8"}}>{selectedVisit.duration}</span>
              </div>
              <div style={{marginBottom:12}}>
                <h4 style={{fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:4}}>Location</h4>
                <p style={{fontSize:13,color:"#e2e8f0"}}>{selectedVisit.location}</p>
              </div>
              <div style={{marginBottom:12}}>
                <h4 style={{fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:4}}>Visitors</h4>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>{selectedVisit.visitors.map(v => <span key={v} className="badge badge-blue">{v}</span>)}</div>
              </div>
              <div style={{marginBottom:12}}>
                <h4 style={{fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:4}}>Notes</h4>
                <p style={{fontSize:13,color:"#e2e8f0",lineHeight:1.6}}>{selectedVisit.notes}</p>
              </div>
              <div>
                <h4 style={{fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:4}}>Expenses</h4>
                <p style={{fontSize:16,fontWeight:700,color:selectedVisit.expenses > 0 ? "#f97316" : "#64748b"}}>{selectedVisit.expenses > 0 ? "$" + selectedVisit.expenses : "No expenses"}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
